import type { ApiFetchWith } from "./apiFetch.js";
import type { NewWorkout } from "./schemas/workout.js";
import { splitWorkout } from "./workoutSplit.js";

// Minimal API surface persistWorkout needs — same shape exposed by both
// apps/web/src/api.ts and apps/mobile/src/api/index.ts, plus the replay key
// documented on PersistWorkoutStep below.
export type ApiFetch = ApiFetchWith<{
  /** Optional. Stable Idempotency-Key for replays. */
  idempotencyKey?: string;
}>;

/**
 * Identifies which API call a Idempotency-Key applies to inside a
 * single persistWorkout run. The mobile outbox uses this to derive
 * a stable key per step from the outbox item id, so a retry of the
 * same item replays each underlying request idempotently.
 */
export type PersistWorkoutStep =
  | { kind: "session" }
  | { kind: "sets-batch"; chunk: number }
  | { kind: "old-delete" }
  | { kind: "orphan-cleanup" };

export interface PersistWorkoutArgs {
  /** Already-validated NewWorkout — caller runs Zod safeParse. */
  workout: NewWorkout;
  /** When editing an existing session, its id; otherwise null. */
  editingId: string | null;
  apiFetch: ApiFetch;
  /**
   * Generates the new session id. Caller-controlled so the same id
   * round-trips through any retry / outbox replay path with a stable
   * Idempotency-Key. Web passes `crypto.randomUUID`; mobile passes a
   * crypto.randomUUID under the `react-native-get-random-values`
   * polyfill.
   */
  generateId: () => string;
  /**
   * Optional logging hooks. Both default to silent. Web wires
   * console.warn so the orphan / stale-edit failures show up in
   * devtools; mobile keeps them quiet.
   */
  onOrphanCleanupFailed?: (sessionId: string, err: unknown) => void;
  onOldSessionDeleteFailed?: (sessionId: string, err: unknown) => void;
  /**
   * Optional. Returns the Idempotency-Key for a given step. The
   * mobile outbox supplies stable keys derived from the outbox item
   * id so each retry replays each underlying call idempotently. Web
   * leaves this undefined and apiFetch generates a fresh key per
   * request — every save is a brand new operation.
   */
  idempotencyKeyFor?: (step: PersistWorkoutStep) => string | undefined;
}

export interface PersistWorkoutResult {
  sessionId: string;
  /** Server-stamped updatedAt from the POST /sessions response. */
  serverUpdatedAt: string | undefined;
  /**
   * True iff the new session + sets posted but the old session DELETE
   * failed (edit-mode only). Caller decides whether to surface a
   * "duplicate left in history" flash.
   */
  oldSessionDeleteFailed: boolean;
}

const SETS_CHUNK = 100;

/**
 * Native write path used by both web and mobile loggers. Replaces the
 * previous destructive wipe-and-replay (DELETE THEN POST), which left
 * the user with an empty workout when the network died between the
 * two calls — no recovery.
 *
 * Steps:
 *   1. POST /sessions for the envelope.
 *   2. POST /sets/batch in chunks of 100 for the LogSets.
 *   3. On failure of step 1 or 2: best-effort DELETE the freshly
 *      created session so we don't leak an orphan. The original
 *      session (when editing) stays intact, the user can retry.
 *   4. On success: if editing, DELETE the old session id. Failure
 *      here is non-fatal — caller decides whether to surface the
 *      duplicate.
 */
export async function persistWorkout(
  args: PersistWorkoutArgs,
): Promise<PersistWorkoutResult> {
  const {
    workout,
    editingId,
    apiFetch,
    generateId,
    onOrphanCleanupFailed,
    onOldSessionDeleteFailed,
    idempotencyKeyFor,
  } = args;

  const sessionId = generateId();
  const { session, sets } = splitWorkout({ ...workout, id: sessionId });
  const keyFor = (step: PersistWorkoutStep): string | undefined =>
    idempotencyKeyFor?.(step);

  let sessionPosted = false;
  let serverUpdatedAt: string | undefined;

  try {
    const resp = await apiFetch<unknown>("/api/sessions", {
      method: "POST",
      body: session,
      idempotencyKey: keyFor({ kind: "session" }),
    });
    sessionPosted = true;
    if (resp && typeof resp === "object") {
      const ru = (resp as { updatedAt?: unknown }).updatedAt;
      if (typeof ru === "string") serverUpdatedAt = ru;
    }
    let chunk = 0;
    for (let i = 0; i < sets.length; i += SETS_CHUNK) {
      const slice = sets.slice(i, i + SETS_CHUNK);
      await apiFetch<unknown>("/api/sets/batch", {
        method: "POST",
        body: slice,
        idempotencyKey: keyFor({ kind: "sets-batch", chunk }),
      });
      chunk++;
    }
  } catch (writeErr) {
    if (sessionPosted) {
      try {
        await apiFetch<unknown>(
          `/api/sessions/${encodeURIComponent(sessionId)}`,
          {
            method: "DELETE",
            idempotencyKey: keyFor({ kind: "orphan-cleanup" }),
          },
        );
      } catch (cleanupErr) {
        onOrphanCleanupFailed?.(sessionId, cleanupErr);
      }
    }
    throw writeErr;
  }

  let oldSessionDeleteFailed = false;
  if (editingId !== null) {
    try {
      await apiFetch<unknown>(
        `/api/sessions/${encodeURIComponent(editingId)}`,
        {
          method: "DELETE",
          idempotencyKey: keyFor({ kind: "old-delete" }),
        },
      );
    } catch (deleteErr) {
      oldSessionDeleteFailed = true;
      onOldSessionDeleteFailed?.(editingId, deleteErr);
    }
  }

  return { sessionId, serverUpdatedAt, oldSessionDeleteFailed };
}
